import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaEuroSign, FaClock, FaCalendar, FaMapMarkerAlt, FaTh, FaList, FaPhoneAlt, FaArrowRight, FaTimes, FaHandshake, FaChevronLeft, FaChevronRight, FaSlidersH, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import './machinery.css';
import { getAnnouncementUrl } from '../../utils/slugify';
import { useApp } from '../../context/AppContext';
import { getApiEndpoint, getUploadUrl } from '../../config/api';

const Machinery = ({ filters, setFilters, isExpanded, setIsExpanded, activeFiltersCount = 0 }) => {
  const { t } = useApp();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid'); // Default to grid view
  const [machineryData, setMachineryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteringLoading, setFilteringLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState({});

  // Fetch announcements from API
  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Debug: Log filters when they change
  useEffect(() => {
    console.log('Filters changed:', filters);
    
    // Show filtering animation when filters change (but not on initial load)
    if (!loading && filters) {
      setFilteringLoading(true);
      const timer = setTimeout(() => {
        setFilteringLoading(false);
      }, 1200); // Longer loading animation
      
      return () => clearTimeout(timer);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/announcements/all'));
      const data = await response.json();
      
      if (data.success) {
        // Transform API data to match component structure
        const transformedData = data.announcements.map(ann => {
          const imageArray = ann.images ? ann.images.split(',').map(img => img.trim()) : [];
          return {
            id: ann.id,
            title: ann.title, // Keep original title for URL slugs
            name: `${ann.manufacturer} ${ann.model}`,
            category: ann.category,
            subcategory: ann.subcategory,
            subsubcategory: ann.subsubcategory,
            created_at: ann.created_at,
            price: parseFloat(ann.price),
            year: ann.year,
            hours: ann.hours,
            hoursUnit: ann.hoursUnit || 'hours',
            condition: ann.condition,
            location: ann.location,
            images: imageArray,
            image: imageArray.length > 0 ? getUploadUrl(imageArray[0]) : 'https://images.unsplash.com/photo-1581094271901-8022df4466f9?w=400&h=300&fit=crop',
            specs: {
              power: ann.power ? `${ann.power} CP` : 'N/A',
              weight: ann.weight ? `${ann.weight} tone` : 'N/A',
              bucket: ann.capacity ? `${ann.capacity} m³` : 'N/A'
            },
            features: ann.features ? ann.features.split('\n').filter(f => f.trim()) : [],
            description: ann.description,
            contact: ann.contact,
            negotiable: ann.negotiable,
            featured: ann.featured  // Add featured status
          };
        });
        setMachineryData(transformedData);
        
        // Initialize image indices
        const initialIndices = {};
        transformedData.forEach(item => {
          initialIndices[item.id] = 0;
        });
        setCurrentImageIndex(initialIndices);
      } else {
        setError('Failed to load announcements');
      }
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setError('Server connection error');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort machinery
  const filteredMachinery = machineryData.filter(machine => {
    // Search query filter - normalize by removing spaces for better matching
    const normalizedQuery = searchQuery.toLowerCase().replace(/\s+/g, '');
    const normalizedName = machine.name.toLowerCase().replace(/\s+/g, '');
    const normalizedDescription = machine.description ? machine.description.toLowerCase().replace(/\s+/g, '') : '';
    
    const matchesSearch = normalizedName.includes(normalizedQuery) ||
                          normalizedDescription.includes(normalizedQuery);
    
    // Category filter - hierarchical matching
    let matchesCategory = !filters || !filters.category || filters.category === 'all';
    
    if (!matchesCategory && filters.category) {
      const categories = Array.isArray(filters.category) ? filters.category : [filters.category];
      
      // First, determine if ANY subsubcategory or subcategory filters are selected
      // This determines the filtering level we should use
      const hasAnySubsubcategoryFilter = categories.some(cat => {
        // Check if this category exists as a subsubcategory in any machine
        return machineryData.some(m => m.subsubcategory && m.subsubcategory.toLowerCase() === cat.toLowerCase());
      });
      
      const hasAnySubcategoryFilter = categories.some(cat => {
        // Check if this category exists as a subcategory in any machine
        return machineryData.some(m => m.subcategory && m.subcategory.toLowerCase() === cat.toLowerCase());
      });
      
      // Apply hierarchical matching based on the most specific filter level selected
      if (hasAnySubsubcategoryFilter) {
        // If ANY subsubcategory is selected, ONLY match subsubcategories
        matchesCategory = machine.subsubcategory && 
                         categories.some(cat => cat.toLowerCase() === machine.subsubcategory.toLowerCase());
      } else if (hasAnySubcategoryFilter) {
        // If ANY subcategory is selected (and no subsubcategory), ONLY match subcategories
        matchesCategory = machine.subcategory && 
                         categories.some(cat => cat.toLowerCase() === machine.subcategory.toLowerCase());
      } else {
        // Otherwise, match main categories
        matchesCategory = categories.some(cat => cat.toLowerCase() === machine.category.toLowerCase());
      }
    }
    
    // Featured filter
    const matchesFeatured = !filters || !filters.featured || machine.featured === 1;
    
    // Condition filter - case insensitive comparison
    const matchesCondition = !filters || !filters.condition || !Array.isArray(filters.condition) || filters.condition.length === 0 || 
                            filters.condition.some(filterCond => 
                              machine.condition.toLowerCase().includes(filterCond.toLowerCase()) ||
                              filterCond.toLowerCase().includes(machine.condition.toLowerCase())
                            );
    
    // Price range filter
    const matchesPriceRange = !filters || !filters.priceRange ||
                             (!filters.priceRange.min && !filters.priceRange.max) ||
                             ((!filters.priceRange.min || machine.price >= parseFloat(filters.priceRange.min)) &&
                              (!filters.priceRange.max || machine.price <= parseFloat(filters.priceRange.max)));
    
    // Year range filter
    const matchesYearRange = !filters || !filters.yearRange ||
                            (!filters.yearRange.min && !filters.yearRange.max) ||
                            ((!filters.yearRange.min || machine.year >= parseInt(filters.yearRange.min)) &&
                             (!filters.yearRange.max || machine.year <= parseInt(filters.yearRange.max)));
    
    // Hours range filter
    const matchesHoursRange = !filters || !filters.hoursRange ||
                             (!filters.hoursRange.min && !filters.hoursRange.max) ||
                             ((!filters.hoursRange.min || machine.hours >= parseInt(filters.hoursRange.min)) &&
                              (!filters.hoursRange.max || machine.hours <= parseInt(filters.hoursRange.max)));
    
    // Brands filter (manufacturer) - case insensitive
    const matchesBrands = !filters || !filters.brands || !Array.isArray(filters.brands) || filters.brands.length === 0 || 
                         filters.brands.some(brand => machine.name.toLowerCase().includes(brand.toLowerCase()));
    
    return matchesSearch && matchesCategory && matchesFeatured && matchesCondition && 
           matchesPriceRange && matchesYearRange && matchesHoursRange && matchesBrands;
  }).sort((a, b) => {
    switch(sortBy) {
      case 'price-low': return a.price - b.price;
      case 'price-high': return b.price - a.price;
      case 'year-new': return b.year - a.year;
      case 'year-old': return a.year - b.year;
      case 'hours-low': return a.hours - b.hours;
      default: return b.id - a.id;
    }
  });

  // Image navigation handlers
  const handlePrevImage = (e, machineId, totalImages) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => ({
      ...prev,
      [machineId]: prev[machineId] > 0 ? prev[machineId] - 1 : totalImages - 1
    }));
  };

  const handleNextImage = (e, machineId, totalImages) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => ({
      ...prev,
      [machineId]: prev[machineId] < totalImages - 1 ? prev[machineId] + 1 : 0
    }));
  };

  // Clear all filters function
  const handleClearAllFilters = () => {
    setSearchQuery('');
    setFilters({
      category: 'all',
      condition: [],
      priceRange: { min: '', max: '' },
      yearRange: { min: '', max: '' },
      hoursRange: { min: '', max: '' },
      brands: []
    });
    navigate('/machines', { replace: true });
  };

  return (
    <section className="machinerypage-machinery-section">
      <div className="machinerypage-container">
        {/* Loading State */}
        {loading && (
          <div className="machinerypage-loading-state">
            <div className="machinerypage-loading-spinner"></div>
            <p>{t.machineryPage.loadingMachinery}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="machinerypage-error-state">
            <p>{error}</p>
            <button onClick={fetchAnnouncements} className="machinerypage-retry-btn">{t.machineryPage.errorRetry}</button>
          </div>
        )}

        {/* Main Content */}
        {!loading && !error && (
          <>
            {/* Search and Controls Bar */}
            <div className="machinerypage-search-controls-wrapper">
          <div className="machinerypage-search-input-wrapper">
            <FaSearch className="machinerypage-search-icon" />
            <input
              type="text"
              className="machinerypage-search-input"
              placeholder={t.machineryPage.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="machinerypage-clear-search" onClick={() => setSearchQuery('')}>
                <FaTimes />
              </button>
            )}
          </div>
          
          <div className="machinerypage-controls-group">
            {/* Advanced Filters Button */}
            <button 
              className={`machinerypage-advanced-filter-btn-machinery ${isExpanded ? 'active' : ''}`}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <FaSlidersH />
              <span>{t.filters.advancedFiltersButton}</span>
              {activeFiltersCount > 0 && (
                <span className="machinerypage-filter-badge-machinery">{activeFiltersCount}</span>
              )}
              {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            
            <div className="machinerypage-view-mode-switcher">
              <button 
                className={`machinerypage-view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title={t.machineryPage.gridView}
              >
                <FaTh />
              </button>
              <button 
                className={`machinerypage-view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title={t.machineryPage.listView}
              >
                <FaList />
              </button>
            </div>

            <select 
              className="machinerypage-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">{t.machineryPage.sortBy.newest}</option>
              <option value="price-low">{t.machineryPage.sortBy.priceLow}</option>
              <option value="price-high">{t.machineryPage.sortBy.priceHigh}</option>
              <option value="year-new">{t.machineryPage.sortBy.yearNew}</option>
              <option value="year-old">{t.machineryPage.sortBy.yearOld}</option>
              <option value="hours-low">{t.machineryPage.sortBy.hoursLow}</option>
            </select>
          </div>
        </div>

        {/* Machinery Grid */}
        <div className="machinerypage-machinery-main-content">
          {/* Filtering Loading Overlay */}
          {filteringLoading && (
            <div className="machinerypage-filtering-loading-overlay">
              <div className="machinerypage-filtering-content">
                <div className="machinerypage-filtering-spinner"></div>
                <p>{t.machineryPage.applyingFilters}</p>
              </div>
              <div className="machinerypage-filtering-placeholder-grid">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="machinerypage-filtering-placeholder-card">
                    <div className="machinerypage-filtering-placeholder-image"></div>
                    <div className="machinerypage-filtering-placeholder-content">
                      <div className="machinerypage-filtering-placeholder-line long"></div>
                      <div className="machinerypage-filtering-placeholder-line medium"></div>
                      <div className="machinerypage-filtering-placeholder-line short"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="machinerypage-results-info">
            <h3 className="machinerypage-results-count">
              {filteredMachinery.length} {filteredMachinery.length === 1 ? t.machineryPage.machineFound : t.machineryPage.machinesFound}
            </h3>
          </div>

          {filteredMachinery.length === 0 ? (
            <div className="machinerypage-no-results-message">
              <div className="machinerypage-no-results-icon">🔍</div>
              <h3>{t.machineryPage.noResultsTitle}</h3>
              <p>{t.machineryPage.noResultsMessage}</p>
              <button 
                className="machinerypage-reset-search-btn"
                onClick={handleClearAllFilters}
              >
                {t.machineryPage.clearFilters}
              </button>
            </div>
          ) : (
            <div className={`machinerypage-machinery-grid ${viewMode}`}>
              {filteredMachinery.map((machine, index) => {
                const currentIndex = currentImageIndex[machine.id] || 0;
                const currentImageUrl = machine.images && machine.images.length > 0 
                  ? getUploadUrl(machine.images[currentIndex])
                  : machine.image;
                
                return (
                <div 
                  key={machine.id} 
                  className={`machinerypage-machinery-card ${viewMode} ${machine.featured ? 'machinerypage-hotspot-featured' : ''}`}
                  onClick={() => navigate(getAnnouncementUrl(machine.id, machine.title, machine.category, machine.subcategory, machine.created_at))}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Image Section */}
                  <div className="machinerypage-machinery-image-wrapper">
                    {machine.featured === 1 && (
                      <div className="machinerypage-hotspot-ribbon">
                        <span>{t.machineryPage.hotspotBadge}</span>
                      </div>
                    )}
                    <img 
                      key={`${machine.id}-${currentIndex}`}
                      src={currentImageUrl} 
                      alt={machine.name}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                      }}
                    />
                    <div className="machinerypage-image-overlay">
                      <span className="machinerypage-condition-badge">{machine.condition}</span>
                    </div>
                    
                    {/* Image Navigation - Only show if multiple images */}
                    {machine.images && machine.images.length > 1 && (
                      <>
                        <button 
                          className="machinerypage-image-nav-btn machinerypage-prev-btn"
                          onClick={(e) => handlePrevImage(e, machine.id, machine.images.length)}
                          aria-label="Previous image"
                        >
                          <FaChevronLeft />
                        </button>
                        <button 
                          className="machinerypage-image-nav-btn machinerypage-next-btn"
                          onClick={(e) => handleNextImage(e, machine.id, machine.images.length)}
                          aria-label="Next image"
                        >
                          <FaChevronRight />
                        </button>
                        <div className="machinerypage-image-counter">
                          {currentIndex + 1} / {machine.images.length}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="machinerypage-machinery-info">
                    {/* Header */}
                    <div className="machinerypage-machinery-header-section">
                      <span className="machinerypage-machinery-category-tag">
                        {machine.subcategory || machine.category}
                      </span>
                      <h3 className="machinerypage-machinery-title">{machine.name}</h3>
                    </div>

                    {/* Quick Stats */}
                    <div className="machinerypage-machinery-quick-stats">
                      <div className="machinerypage-stat-item">
                        <FaCalendar className="machinerypage-stat-icon" />
                        <div className="machinerypage-stat-content">
                          <span className="machinerypage-stat-label">{t.machineryPage.year}</span>
                          <span className="machinerypage-stat-value">{machine.year}</span>
                        </div>
                      </div>
                      {machine.hours && (
                        <div className="machinerypage-stat-item">
                          <FaClock className="machinerypage-stat-icon" />
                          <div className="machinerypage-stat-content">
                            <span className="machinerypage-stat-label">
                              {machine.hoursUnit === 'km' ? 'Kilometri' : t.machineryPage.hours}
                            </span>
                            <span className="machinerypage-stat-value">
                              {machine.hours.toLocaleString()}{machine.hoursUnit === 'km' ? ' Km' : 'H'}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="machinerypage-stat-item">
                        <FaMapMarkerAlt className="machinerypage-stat-icon" />
                        <div className="machinerypage-stat-content">
                          <span className="machinerypage-stat-label">{t.machineryPage.location}</span>
                          <span className="machinerypage-stat-value">{machine.location}</span>
                        </div>
                      </div>
                    </div>

                    {/* Specifications */}
                    <div className="machinerypage-machinery-specifications">
                      {machine.specs.power !== 'N/A' && (
                        <div className="machinerypage-spec-badge">
                          <span className="machinerypage-spec-icon">⚡</span>
                          <span>{t.machineryPage.power}: {machine.specs.power}</span>
                        </div>
                      )}
                      {machine.specs.weight !== 'N/A' && (
                        <div className="machinerypage-spec-badge">
                          <span className="machinerypage-spec-icon">⚖️</span>
                          <span>{t.machineryPage.weight}: {machine.specs.weight}</span>
                        </div>
                      )}
                      {machine.specs.bucket !== 'N/A' && (
                        <div className="machinerypage-spec-badge">
                          <span className="machinerypage-spec-icon">🪣</span>
                          <span>{t.machineryPage.bucket}: {machine.specs.bucket}</span>
                        </div>
                      )}
                    </div>

                    {/* Features - List View Only */}
                    {viewMode === 'list' && machine.features.length > 0 && (
                      <div className="machinerypage-machinery-features-list">
                        {machine.features.slice(0, 4).map((feature, idx) => (
                          <span key={idx} className="machinerypage-feature-item">
                            <span className="machinerypage-checkmark">✓</span> {feature}
                          </span>
                        ))}
                        {machine.features.length > 4 && (
                          <span className="machinerypage-more-features">+{machine.features.length - 4} {t.machineryPage.moreFeatures}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Price and Actions */}
                  <div className="machinerypage-machinery-actions-section">
                    <div className="machinerypage-price-container">
                      {machine.negotiable === 1 && (
                        <span className="machinerypage-negotiable-badge-top">
                          <FaHandshake /> {t.machineryPage.negotiable}
                        </span>
                      )}
                      <span className="machinerypage-price-label">{t.machineryPage.price}</span>
                      <div className="machinerypage-price-value">
                        <FaEuroSign className="machinerypage-euro-icon" />
                        <span className="machinerypage-price-amount">{machine.price.toLocaleString()}</span>
                      </div>
                    </div>
                    
                    <div className="machinerypage-action-buttons">
                      <button 
                        className="machinerypage-btn-primary-action"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaPhoneAlt /> {t.machineryPage.callNow}
                      </button>
                      <button 
                        className="machinerypage-btn-secondary-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(getAnnouncementUrl(machine.id, machine.title, machine.category, machine.subcategory, machine.created_at));
                        }}
                      >
                        {t.machineryPage.viewDetails} <FaArrowRight />
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Machinery;












